'use client';

import { css } from 'styled-components';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import { getGallerySourceOptimization, type HeroBox } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { useIsLgOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';

type EvoTopSecretGalleryProps = {
  images: string[];
  /**
   * The boxes this gallery is painted at, for the same reason EvoHeroGallery passes them: without
   * them the source set describes the *classic* hero's shape, so the evo hero asks for an image
   * sized for a page it is not on. The hotel-info modal passes none, which matches the ordinary
   * gallery it stands in for there (EvoHotelDetailsGallery also omits them).
   */
  boxes?: HeroBox[];
} & React.HTMLAttributes<HTMLElement>;

/**
 * MPH-935: the hero gallery for a Top Secret Hotel.
 *
 * A Top Secret rate is opaque — the customer books before learning which hotel it is — so the page
 * may not reveal the property. That rules out the ordinary Evo gallery on three counts, and this
 * component is the opposite of it on each:
 *
 * - one image, never the deck. travel-api sends exactly one image for a TSH hotel, and the blur is
 *   what makes it safe to show at all (getGallerySourceOptimization's isTopSecret adds it).
 * - no expanding. The Evo gallery opens a full-resolution overlay on click; here there is nothing
 *   the customer is allowed to look at more closely, so the image is inert.
 * - the Top Secret mark over it, so the blur reads as deliberate rather than as a broken photo.
 *
 * Mirrors the legacy TSHGallery, which the Evo layout had no equivalent of — its gallery passes
 * `isTopSecret: false` unconditionally and so served the hotel unblurred.
 */
export function EvoTopSecretGallery({ images, boxes, ...rest }: EvoTopSecretGalleryProps) {
  const { localization, commonData } = usePageWrapperData();
  const { imageBasePath } = localization;
  const isMobile = !useIsLgOrGreater(commonData.isDesktopDevice);

  // Only ever the first image: a TSH response carries one, and slicing keeps a hotel that somehow
  // carries more from leaking the rest.
  const [image] = getGallerySourceOptimization({
    images: images.slice(0, 1),
    isMobile,
    isTopSecret: true,
    totalImages: 1,
    boxes,
  });

  return (
    <div
      {...rest}
      css={css`
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background-color: ${(p) => p.theme.kallisto.color.background.product.backgroundTopSecretHotels};
      `}
    >
      {image && (
        <Picture
          alt=""
          url={image.url}
          sources={image.sources}
          loading="eager"
          css={`
            width: 100%;
            height: 100%;
            object-fit: cover;
          `}
        />
      )}
      <div
        css={`
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        {/* Decorative on purpose, unlike legacy TSHGallery's alt="top secret": the mark repeats what
            the Top Secret banner directly below the hero already says in words, and the blurred
            photo behind it carries nothing to describe. */}
        <img
          css={`
            width: 96px;
            height: 96px;
          `}
          src={`${imageBasePath}logo-image-tsh-white.svg`}
          alt=""
        />
      </div>
    </div>
  );
}
